import * as React from 'react';
import {
  orderBy, map, trim,
} from 'lodash';
import moment from 'moment';
import xss from 'xss';
import { logger } from '@common';
import { MemberCommentsQuery_member_comments } from '@/app/queries/types/MemberCommentsQuery';
import { Avatar, AvatarFallback } from '@/shadcn/components/ui/avatar';
import { Button } from '@/shadcn/components/ui/button';
import { RichTextEditor } from '@/app/refresh-components/RichTextEditor';
import UserAvatar from '@/app/refresh-components/UserAvatar';
import Spinner from '@/app/refresh-components/Spinner';
import { useMemberCommentsQuery, useSaveMemberCommentMutation } from '@/app/hooks';
import { useAuth } from '@/context/authContext';
import { useMessagingContext } from '@/hooks';
import { RichTextVisualizer } from '@/app/components/RichTextEditor/Visualizer';
import { cn } from '@/shadcn/lib/utils';

const { useMemo, useState } = React;
const aiqLogo = require('@/app/assets/svgs/aiq-logomark.svg');

interface CommentItemProps {
  comment: MemberCommentsQuery_member_comments;
  isOwn: boolean;
}

const CommentItem: React.FC<CommentItemProps> = ({ comment, isOwn }) => {
  return (
    <div className={cn('flex gap-4', isOwn ? 'justify-end' : 'justify-start')}>
      <div className={cn(
        'flex-1 p-4 rounded-2xl max-w-[80%] flex gap-4',
        isOwn ? 'bg-blue-1' : 'bg-grey-1'
      )}
      >
        {comment.user ? (
          <UserAvatar
            name={comment.user.name}
            className="w-8 h-8"
          />
        ) : (
          <Avatar className="w-8 h-8">
            <AvatarFallback className="bg-grey-3">
              <img src={aiqLogo} className="w-4 h-4" />
            </AvatarFallback>
          </Avatar>
        )}
        <div>
          <div className="text-grey-5 text-xs font-normal mb-1">
            {comment.user?.name}
          </div>
          <div className="text-grey-6 text-sm font-normal mb-2">
            <RichTextVisualizer>
              {xss(comment.text)}
            </RichTextVisualizer>
          </div>
          <div className="text-grey-5 text-xs font-normal">
            {moment(comment.createdDate).format('MMM D YYYY, h:mm a')}
          </div>
        </div>
      </div>
    </div>
  );
};

interface Props {
  memberId: number;
}

export const Notes: React.FC<Props> = ({
  memberId,
}) => {
  const [commentText, setCommentText] = useState<string>('');

  const {
    data: {
      member: {
        comments = null,
      } = {},
    } = {},
    refetch: refetchComments,
    loading: isLoading,
  } = useMemberCommentsQuery(memberId);
  const [saveMemberComment, { loading: isSaving }] = useSaveMemberCommentMutation();
  const { showGenericErrorMessage } = useMessagingContext();
  const { user } = useAuth();

  const canSave = useMemo(() => !!trim(commentText), [commentText]);
  const isMessageEmpty = !commentText || commentText.trim() === '' || commentText === '<p><br></p>';

  const addComment = async () => {
    try {
      await saveMemberComment({
        variables: {
          memberId,
          comment: {
            userId: user.sub,
            text: commentText,
          },
        },
      });
      setCommentText('');
      await refetchComments();
    } catch (error) {
      logger.error(error);
      showGenericErrorMessage();
    }
  };

  const sortedComments = useMemo(() => {
    return orderBy(comments, 'createdDate', 'asc');
  }, [comments]);

  const isOwnComment = (comment: MemberCommentsQuery_member_comments) => comment.user?.id === user?.sub;

  return (
    <div className="flex flex-col gap-4 pb-20">
      {isLoading && (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      )}

      <div className="flex flex-col gap-4">
        {map(sortedComments, (comment) => (
          <CommentItem
            key={comment.id}
            comment={comment}
            isOwn={isOwnComment(comment)}
          />
        ))}
      </div>

      <div className="relative overflow-hidden">
        <RichTextEditor
          value={commentText}
          onChange={setCommentText}
          placeholder="Enter an internal comment for your team about this member"
          className="border-0"
        />
        <Button
          variant="ghost"
          onClick={addComment}
          disabled={!canSave || isMessageEmpty}
          loading={isSaving}
          className="absolute bottom-1 right-2 px-3 py-1 z-10"
        >
          Reply
        </Button>
      </div>
    </div>
  );
};
